import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useIsTextClamped } from '@/shared/hooks/useIsTextClamped';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    textWrapper: { position: 'relative' },
    readMore: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      backgroundColor: theme.colors.surfaceContainerLowest,
      paddingLeft: 12
    },
    readMoreText: {
      color: theme.colors.primary,
      fontWeight: 'bold'
    }
  });

type Props = {
  text: string;
  maxLines?: number;
  onMore?: () => void;
};

export const ReadMoreText: FC<Props> = ({ text, maxLines, onMore }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { isClamped, onTextLayout, ref } = useIsTextClamped(maxLines);

  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'components'
  });

  if (maxLines == undefined) {
    return (
      <Text onPress={onMore}>
        <Text variant="bodyLarge">{text}</Text>
      </Text>
    );
  }

  return (
    <View style={styles.textWrapper} ref={ref}>
      <Text
        variant="bodyLarge"
        numberOfLines={maxLines}
        onTextLayout={onTextLayout}
      >
        {text}
      </Text>
      {isClamped && (
        <Pressable
          style={styles.readMore}
          onPress={onMore}
          accessibilityRole="link"
        >
          <Text variant="bodyLarge" style={styles.readMoreText}>
            {t('readMore')}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

ReadMoreText.displayName = 'ReadMoreText';
